import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Plus, Trash2, Upload, Save, X, Edit2 } from 'lucide-react';
import { assetApi, type Asset } from '@/services/assetApi';

export default function AssetManager() {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    loadAssets();
  }, []);

  const loadAssets = async () => {
    try {
      setLoading(true);
      const data = await assetApi.getAssets();
      setAssets(data);
    } catch (error) {
      console.error('Failed to load assets:', error);
      // Don't show alert if it's just an empty list
      setAssets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    try {
      setUploadingFile(true);
      const url = await assetApi.uploadAssetImage(file);
      return url;
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Hiba történt a fájl feltöltésekor');
      return null;
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSaveAsset = async (assetData: Partial<Asset>) => {
    try {
      if (editingAsset && !isCreating) {
        // Update existing
        await assetApi.updateAsset(editingAsset.id, assetData);
      } else {
        // Create new
        await assetApi.createAsset(assetData as Omit<Asset, 'id' | 'createdAt' | 'updatedAt'>);
      }
      
      await loadAssets();
      setEditingAsset(null);
      setIsCreating(false);
    } catch (error) {
      console.error('Save failed:', error);
      alert('Hiba történt a mentés során');
    }
  };

  const handleDeleteAsset = async (id: string) => {
    if (!confirm('Biztosan törölni szeretnéd ezt az asset-et?')) return;
    
    try {
      await assetApi.deleteAsset(id);
      await loadAssets();
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Hiba történt a törlés során');
    }
  };

  const startCreating = () => {
    setIsCreating(true);
    setEditingAsset({
      id: '',
      marker: '',
      url: '',
      alt: '',
      description: '',
      formatting: {
        width: '',
        height: '',
        className: '',
        style: {},
      },
      createdAt: '',
      updatedAt: '',
    });
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-600">Asset-ek betöltése...</p>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-light mb-2">Asset Manager</h2>
          <p className="text-gray-600 text-sm">
            Kezeld a szövegbe ágyazható képeket és asset-eket
          </p>
        </div>
        <Button onClick={startCreating} disabled={isCreating || editingAsset !== null}>
          <Plus className="w-4 h-4 mr-2" />
          Új Asset
        </Button>
      </div>

      {/* Editor Form */}
      {editingAsset && (
        <AssetEditor
          asset={editingAsset}
          onSave={handleSaveAsset}
          onCancel={() => {
            setEditingAsset(null);
            setIsCreating(false);
          }}
          onFileUpload={handleFileUpload}
          uploadingFile={uploadingFile}
        />
      )}

      {/* Assets List */}
      <div className="space-y-4 mt-8">
        {assets.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            Még nincsenek asset-ek. Hozz létre egyet!
          </div>
        ) : (
          assets.map((asset) => (
            <AssetCard
              key={asset.id}
              asset={asset}
              onEdit={() => {
                setEditingAsset(asset);
                setIsCreating(false);
              }}
              onDelete={() => handleDeleteAsset(asset.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

// Asset Editor Component
function AssetEditor({
  asset,
  onSave,
  onCancel,
  onFileUpload,
  uploadingFile,
}: {
  asset: Asset;
  onSave: (data: Partial<Asset>) => void;
  onCancel: () => void;
  onFileUpload: (file: File) => Promise<string | null>;
  uploadingFile: boolean;
}) {
  const [formData, setFormData] = useState(asset);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const url = await onFileUpload(file);
    if (url) {
      setFormData({ ...formData, url });
    }
  };

  const handleFileChangeEn = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const url = await onFileUpload(file);
    if (url) {
      setFormData({ ...formData, urlEn: url });
    }
  };

  const handleStyleChange = (key: string, value: string) => {
    setFormData({
      ...formData,
      formatting: {
        ...formData.formatting,
        style: {
          ...formData.formatting.style,
          [key]: value,
        },
      },
    });
  };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
      <h3 className="text-lg font-medium mb-4">
        {asset.id ? 'Asset szerkesztése' : 'Új asset létrehozása'}
      </h3>

      <div className="grid grid-cols-2 gap-6">
        {/* Left Column - Basic Info */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Marker neve *
            </label>
            <input
              type="text"
              placeholder="pl: OPTEN_BADGE"
              value={formData.marker}
              onChange={(e) => setFormData({ ...formData, marker: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
            <p className="text-xs text-gray-500 mt-1">
              Használd: [OPTEN_BADGE] a szövegben
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kép feltöltése (HU) *
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              disabled={uploadingFile}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
            {uploadingFile && (
              <p className="text-xs text-blue-600 mt-1">Feltöltés folyamatban...</p>
            )}
          </div>

          {formData.url && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Előnézet (HU)
              </label>
              <img
                src={formData.url}
                alt="Preview"
                className="max-w-full h-32 object-contain border border-gray-300 rounded"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kép feltöltése (EN) - opcionális
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChangeEn}
              disabled={uploadingFile}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              Ha nincs, a magyar változat jelenik meg angolul is
            </p>
          </div>

          {formData.urlEn && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Előnézet (EN)
              </label>
              <img
                src={formData.urlEn}
                alt="Preview EN"
                className="max-w-full h-32 object-contain border border-gray-300 rounded"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Alt szöveg (HU)
            </label>
            <input
              type="text"
              value={formData.alt || ''}
              onChange={(e) => setFormData({ ...formData, alt: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Alt szöveg (EN) - opcionális
            </label>
            <input
              type="text"
              value={formData.altEn || ''}
              onChange={(e) => setFormData({ ...formData, altEn: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Képaláírás (HU) - opcionális
            </label>
            <input
              type="text"
              value={formData.caption || ''}
              onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
            <p className="text-xs text-gray-500 mt-1">
              A kép alatt megjelenő szöveg
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Képaláírás (EN) - opcionális
            </label>
            <input
              type="text"
              value={formData.captionEn || ''}
              onChange={(e) => setFormData({ ...formData, captionEn: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
            <p className="text-xs text-gray-500 mt-1">
              Ha nincs, a magyar változat jelenik meg angolul is
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Leírás (csak adminnak)
            </label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              rows={2}
            />
          </div>
        </div>

        {/* Right Column - Formatting */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Formázási beállítások</h4>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Szélesség
              </label>
              <input
                type="text"
                placeholder="pl: 100px vagy auto"
                value={formData.formatting.width || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    formatting: { ...formData.formatting, width: e.target.value },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Magasság
              </label>
              <input
                type="text"
                placeholder="pl: auto"
                value={formData.formatting.height || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    formatting: { ...formData.formatting, height: e.target.value },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tailwind osztályok
            </label>
            <input
              type="text"
              placeholder="pl: grayscale inline-block"
              value={formData.formatting.className || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  formatting: { ...formData.formatting, className: e.target.value },
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              Képre (vagy figure-re ha van caption) alkalmazott osztályok
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Képaláírás Tailwind osztályok
            </label>
            <input
              type="text"
              placeholder="pl: text-gray-600 mt-2"
              value={formData.formatting.captionClassName || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  formatting: { ...formData.formatting, captionClassName: e.target.value },
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              Csak a képaláírásra alkalmazott osztályok (opcionális)
            </p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Inline stílusok (CSS)
            </label>
            
            {Object.entries(formData.formatting.style || {}).map(([key, value]) => (
              <div key={key} className="flex gap-2">
                <input
                  type="text"
                  value={key}
                  readOnly
                  className="w-1/3 px-2 py-1 border border-gray-300 rounded text-sm bg-gray-50"
                />
                <input
                  type="text"
                  value={value}
                  onChange={(e) => handleStyleChange(key, e.target.value)}
                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    const newStyle = { ...formData.formatting.style };
                    delete newStyle[key];
                    setFormData({
                      ...formData,
                      formatting: { ...formData.formatting, style: newStyle },
                    });
                  }}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ))}

            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const key = prompt('CSS property neve (pl: filter):');
                if (key) {
                  handleStyleChange(key, '');
                }
              }}
              className="w-full"
            >
              + CSS property hozzáadása
            </Button>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>
          Mégse
        </Button>
        <Button
          onClick={() => onSave(formData)}
          disabled={!formData.marker || !formData.url}
        >
          <Save className="w-4 h-4 mr-2" />
          Mentés
        </Button>
      </div>
    </div>
  );
}

// Asset Card Component
function AssetCard({
  asset,
  onEdit,
  onDelete,
}: {
  asset: Asset;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
      <div className="flex items-start gap-4">
        <div className="flex gap-2">
          <div className="relative">
            <img
              src={asset.url}
              alt={asset.alt || asset.marker}
              className="w-24 h-24 object-contain border border-gray-200 rounded"
            />
            <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs px-1 rounded">HU</span>
          </div>
          {asset.urlEn && (
            <div className="relative">
              <img
                src={asset.urlEn}
                alt={asset.altEn || asset.marker}
                className="w-24 h-24 object-contain border border-gray-200 rounded"
              />
              <span className="absolute -top-1 -right-1 bg-green-600 text-white text-xs px-1 rounded">EN</span>
            </div>
          )}
        </div>
        
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-medium text-gray-900">[{asset.marker}]</h3>
              <p className="text-sm text-gray-600 mt-1">
                {asset.description || 'Nincs leírás'}
              </p>
              {asset.alt && (
                <p className="text-xs text-gray-500 mt-1">Alt (HU): {asset.alt}</p>
              )}
              {asset.altEn && (
                <p className="text-xs text-gray-500">Alt (EN): {asset.altEn}</p>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={onEdit}>
                <Edit2 className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={onDelete}>
                <Trash2 className="w-4 h-4 text-red-600" />
              </Button>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            {asset.urlEn && (
              <span className="px-2 py-1 bg-green-50 text-green-700 rounded">
                🌐 Kétnyelvű
              </span>
            )}
            {asset.formatting.width && (
              <span className="px-2 py-1 bg-gray-100 rounded">
                width: {asset.formatting.width}
              </span>
            )}
            {asset.formatting.className && (
              <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded">
                {asset.formatting.className}
              </span>
            )}
            {Object.keys(asset.formatting.style || {}).length > 0 && (
              <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded">
                {Object.keys(asset.formatting.style || {}).length} CSS property
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}